import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const sectionHref = (hash: string) => (isHome ? hash : `/${hash}`);

  const navLinks = [
    { label: "서비스", href: sectionHref("#solutions") },
    { label: "아르트레너 진단", href: "/diagnosis" },
    { label: "플랜", href: sectionHref("#pricing") },
    { label: "FAQ", href: sectionHref("#faq") },
    { label: "프로세스", href: sectionHref("#process") },
    { label: "블로그", href: "/blog" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !isHome ? "glass border-b border-white/5" : "bg-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-6 h-14 md:h-16 flex items-center justify-between gap-2">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2 cursor-pointer flex-shrink-0 min-w-0">
          <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center flex-shrink-0">
            <img
              src="https://public.readdy.ai/ai/img_res/b51260fb-bf77-436f-865d-1c38bc3c656b.png"
              alt="ARTREPRENEUR Logo"
              className="w-7 h-7 md:w-8 md:h-8 object-contain"
            />
          </div>
          <span className="font-display font-bold text-sm md:text-base tracking-[0.12em] md:tracking-widest text-white whitespace-nowrap">
            ARTREPRENEUR
          </span>
        </a>

        {/* Desktop Links */}
        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-sm text-white/60 hover:text-white transition-colors duration-200 font-body whitespace-nowrap cursor-pointer"
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* CTA + Hamburger */}
        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
          <a
            href="/diagnosis"
            className="hidden md:flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-display font-bold text-white whitespace-nowrap cursor-pointer relative overflow-hidden group"
            style={{
              background: "linear-gradient(135deg, #c026d3, #A855F7, #7C3AED)",
              boxShadow: "0 0 20px rgba(168,85,247,0.5), 0 0 40px rgba(168,85,247,0.2), inset 0 1px 0 rgba(255,255,255,0.2)",
            }}
          >
            {/* shimmer sweep */}
            <span
              className="absolute inset-0 pointer-events-none"
              style={{
                background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.18) 50%, transparent 60%)",
                animation: "shimmerBtn 2.6s infinite",
              }}
            />
            {/* pulse ring */}
            <span
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{ animation: "pulseRing 2.6s ease-out infinite", border: "2px solid rgba(168,85,247,0.6)" }}
            />
            <span className="relative z-10 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-white/90 animate-pulse flex-shrink-0" />
              빌딩 신청하기
              <i className="ri-arrow-right-line text-xs group-hover:translate-x-0.5 transition-transform duration-200"></i>
            </span>
          </a>
          <style>{`
            @keyframes shimmerBtn {
              0% { transform: translateX(-100%); }
              60% { transform: translateX(100%); }
              100% { transform: translateX(100%); }
            }
            @keyframes pulseRing {
              0% { transform: scale(1); opacity: 0.7; }
              70% { transform: scale(1.12); opacity: 0; }
              100% { transform: scale(1.12); opacity: 0; }
            }
          `}</style>
          <button
            className="lg:hidden w-9 h-9 flex items-center justify-center text-white cursor-pointer rounded-lg"
            style={{ background: menuOpen ? "rgba(168,85,247,0.15)" : "rgba(255,255,255,0.05)" }}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="메뉴 열기"
          >
            <i className={`text-lg ${menuOpen ? "ri-close-line" : "ri-menu-3-line"}`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          className="lg:hidden border-t border-white/5 px-4 py-5 flex flex-col gap-1"
          style={{ background: "rgba(6,8,15,0.97)", backdropFilter: "blur(20px)" }}
        >
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className="text-sm text-white/70 hover:text-white hover:bg-white/5 transition-all cursor-pointer py-3 px-3 rounded-xl"
            >
              {link.label}
            </a>
          ))}
          <a
            href="/diagnosis"
            onClick={() => setMenuOpen(false)}
            className="mt-3 text-center px-5 py-3 rounded-full text-sm font-medium text-white whitespace-nowrap cursor-pointer"
            style={{ background: "linear-gradient(135deg, #A855F7, #7C3AED)" }}
          >
            빌딩 신청하기
          </a>
        </div>
      )}
    </header>
  );
}
